import React from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import useCustomMediaQuery from "src/hooks/useMediaQuery";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { PrettoSlider } from "./shared/PrettoSlider";
import { useTranslation } from "react-i18next";

interface FilterBarProps {
  statusFilter: string;
  onStatusFilterChange: (event: SelectChangeEvent) => void;
  titleFilter: string;
  onTitleFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  pointRange: number[];
  onPointRangeChange: (event: Event, newValue: number | number[]) => void;
  sortOption: string;
  onSortOptionChange: (event: SelectChangeEvent) => void;
  isSmallView: boolean;
}

const FilterBar = ({
  statusFilter,
  onStatusFilterChange,
  titleFilter,
  onTitleFilterChange,
  pointRange,
  onPointRangeChange,
  sortOption,
  onSortOptionChange,
  isSmallView,
}: FilterBarProps) => {
  const { isMobileView } = useCustomMediaQuery();
  const { t } = useTranslation();

  // Desktop/mobile inline filters (status, search, score range, sort)
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        p: 2,
        gap: 3,
        bgcolor: "background.paper",
        flexWrap: isSmallView || isMobileView ? "wrap" : "nowrap",
      }}
    >
      <FormControl
        sx={{ minWidth: 200, mr: 2 }}
        size="small"
        variant="standard"
      >
        <InputLabel>{t("filterBar.status")}</InputLabel>
        <Select
          value={statusFilter}
          label={t("filterBar.status")}
          onChange={onStatusFilterChange}
        >
          <MenuItem value="">
            <em>{t("filterBar.all")}</em>
          </MenuItem>
          <MenuItem value="rejected">{t("filterBar.rejected")}</MenuItem>
          <MenuItem value="requested">{t("filterBar.requested")}</MenuItem>
          <MenuItem value="accepted">{t("filterBar.accepted")}</MenuItem>
        </Select>
      </FormControl>

      <TextField
        size="small"
        label={t("filterBar.searchName")}
        value={titleFilter}
        onChange={onTitleFilterChange}
        variant="outlined"
        sx={{ minWidth: 200, mr: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PersonSearchIcon color="primary" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ minWidth: 200, mr: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="textSecondary">
          {t("filterBar.score", { min: pointRange[0], max: pointRange[1] })}
        </Typography>
        <PrettoSlider
          value={pointRange}
          onChange={onPointRangeChange}
          valueLabelDisplay="auto"
          min={0}
          max={100}
          isSmallView={false}
        />
      </Box>

      <FormControl sx={{ minWidth: 200 }} variant="standard" size="small">
        <InputLabel>{t("filterBar.sortBy")}</InputLabel>
        <Select
          value={sortOption}
          label={t("filterBar.sortBy")}
          onChange={onSortOptionChange}
        >
          <MenuItem value="name">{t("filterBar.sortOptions.nameAsc")}</MenuItem>
          <MenuItem value="nameDesc">{t("filterBar.sortOptions.nameDesc")}</MenuItem>
          <MenuItem value="score">{t("filterBar.sortOptions.scoreHigh")}</MenuItem>
          <MenuItem value="accepted">{t("filterBar.sortOptions.acceptedFirst")}</MenuItem>
          <MenuItem value="date">{t("filterBar.sortOptions.appliedDate")}</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default FilterBar;
